import { useState, useEffect } from 'react';
import { salesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { printTransaction } from '../lib/printDoc';
import SendEmailButton from '../components/email/SendEmailButton';
import PackingListMenu from '../components/transactions/PackingListMenu';
import PaymentBreakdownPopover from '../components/PaymentBreakdownPopover';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    Search, 
    Loader2, 
    FileText,
    Building,
    Phone,
    Mail,
    MapPin,
    User,
    Eye,
    Trash2,
    Undo2,
    X,
    Printer,
    Package,
} from 'lucide-react';

const paymentMethodLabels = {
    cash: { label: 'Cash', class: 'badge-cash' },
    card: { label: 'Card', class: 'badge-card' },
    bank_transfer: { label: 'Bank Transfer', class: 'badge-bank' },
    cheque: { label: 'Cheque', class: 'bg-[#8B5CF6] text-white' },
    due: { label: 'Due', class: 'badge-due' },
    others: { label: 'Others', class: 'bg-[#6B7280] text-white' },
};

const Transactions = () => {
    const { isAdmin } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    // Admin-only recovery dialog: revert a finalized invoice back to Draft.
    // Kept as an AlertDialog so the operator MUST explicitly confirm before
    // the admin action fires. Never available to non-admin roles.
    const [revertTarget, setRevertTarget] = useState(null);
    const [reverting, setReverting] = useState(false);

    useEffect(() => {
        fetchTransactions();
    }, []);

    useEffect(() => {
        filterTransactions();
    }, [searchQuery, transactions]);

    const fetchTransactions = async () => {
        try {
            const response = await salesAPI.getAll({});
            setTransactions(response.data);
            setFilteredTransactions(response.data);
        } catch (error) {
            console.error('Failed to fetch transactions:', error);
            toast.error('Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const filterTransactions = () => {
        if (!searchQuery.trim()) {
            setFilteredTransactions(transactions);
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const filtered = transactions.filter(t => 
            t.invoice_number?.toLowerCase().includes(query) ||
            t.company_name?.toLowerCase().includes(query) ||
            t.mobile_number?.toLowerCase().includes(query) ||
            t.contact_person?.toLowerCase().includes(query) ||
            t.email_address?.toLowerCase().includes(query)
        );
        setFilteredTransactions(filtered);
    };

    const handleDelete = async (saleId) => {
        if (!window.confirm('Are you sure you want to delete this transaction?')) return;
        
        try {
            await salesAPI.delete(saleId);
            toast.success('Transaction deleted');
            fetchTransactions();
        } catch (error) {
            toast.error('Failed to delete transaction');
        }
    };

    const handleRevertToDraft = async () => {
        if (!revertTarget) return;
        setReverting(true);
        try {
            const { data } = await salesAPI.revertToDraft(revertTarget.id);
            toast.success(
                `${revertTarget.invoice_number} reverted to ${data.status === 'quotation' ? 'Quotation' : 'Draft'}`,
            );
            setRevertTarget(null);
            fetchTransactions();
        } catch (error) {
            const msg = error?.response?.data?.detail || 'Revert failed';
            toast.error(typeof msg === 'string' ? msg : 'Revert failed');
        } finally {
            setReverting(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(value || 0);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatDateTime = (dateStr) => {
        return new Date(dateStr).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getPaymentBadge = (method) => {
        if (method === 'split') {
            return (
                <Badge className="bg-gradient-to-r from-[#2A9D8F] to-[#8B5CF6] text-white font-medium">
                    Split
                </Badge>
            );
        }
        const pm = paymentMethodLabels[method] || { label: method, class: 'bg-gray-500 text-white' };
        return <Badge className={`${pm.class} font-medium`}>{pm.label}</Badge>;
    };

    const getTypeBadge = (type) => {
        if (type === 'invoice') {
            return <Badge variant="outline" className="border-[#1A1A1A] text-[#1A1A1A] font-medium text-[10px]">INVOICE</Badge>;
        }
        return <Badge variant="outline" className="border-[#C08478] text-[#C08478] font-medium text-[10px]">SALE</Badge>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-[#C08478]" />
            </div>
        );
    }

    return (
        <div data-testid="transactions-page" className="space-y-6 animate-fadeIn">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">All Transactions</h1>
                <p className="text-gray-500 mt-1">View and search all sales transactions</p>
            </div>

            {/* Search Box */}
            <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <Input
                            data-testid="transaction-search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by invoice number, company name, or phone number..."
                            className="pl-10 h-12 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478]"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                    {searchQuery && (
                        <p className="text-sm text-gray-500 mt-2">
                            Found {filteredTransactions.length} transaction(s)
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Transactions Table */}
            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="text-[#C08478]" size={20} />
                        Transactions ({filteredTransactions.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {filteredTransactions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <FileText size={48} className="mb-3 opacity-50" />
                            <p className="text-lg font-medium text-[#1A1A1A]">No transactions found</p>
                            <p className="text-sm">
                                {searchQuery ? 'Try a different search term' : 'No sales have been recorded yet'}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[#F5F5F5]">
                                        <TableHead className="font-medium">Invoice</TableHead>
                                        <TableHead className="font-medium">Company</TableHead>
                                        <TableHead className="font-medium">Contact</TableHead>
                                        <TableHead className="font-medium">Amount</TableHead>
                                        <TableHead className="font-medium">Method</TableHead>
                                        <TableHead className="font-medium">Date</TableHead>
                                        <TableHead className="text-right font-medium">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTransactions.map((transaction) => (
                                        <TableRow key={transaction.id} className="hover:bg-[#FAFAFA]" data-testid={`tx-row-${transaction.invoice_number}`}>
                                            <TableCell className="font-mono text-sm font-medium">
                                                <div className="flex items-center gap-2">
                                                    {transaction.invoice_number}
                                                    {getTypeBadge(transaction.type || 'sale')}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-[#1A1A1A]">
                                                        {transaction.company_name || '-'}
                                                    </p>
                                                    {transaction.contact_person && (
                                                        <p className="text-xs text-gray-500">
                                                            {transaction.contact_person}
                                                        </p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm">
                                                    {transaction.mobile_number && (
                                                        <p className="text-gray-600">{transaction.mobile_number}</p>
                                                    )}
                                                    {transaction.email_address && (
                                                        <p className="text-gray-500 text-xs">{transaction.email_address}</p>
                                                    )}
                                                    {!transaction.mobile_number && !transaction.email_address && '-'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-bold text-[#1A1A1A] euro-amount">
                                                    {formatCurrency(transaction.amount)}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    {getPaymentBadge(transaction.payment_method)}
                                                    <PaymentBreakdownPopover
                                                        sale={transaction}
                                                        testIdSuffix={transaction.invoice_number}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-gray-600 text-sm">
                                                {formatDate(transaction.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => setSelectedTransaction(transaction)}
                                                        data-testid={`view-tx-${transaction.invoice_number}`}
                                                        className="text-gray-400 hover:text-[#C08478]"
                                                    >
                                                        <Eye size={16} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => printTransaction(transaction, { language: transaction.customer_document_language || 'fr' })}
                                                        data-testid={`print-tx-${transaction.invoice_number}`}
                                                        className="text-gray-400 hover:text-[#1A1A1A]"
                                                    >
                                                        <Printer size={16} />
                                                    </Button>
                                                    <PackingListMenu transactionId={transaction.id} compact testIdPrefix="pkg-row" />
                                                    {isAdmin() && transaction.status === 'final' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => setRevertTarget(transaction)}
                                                            data-testid={`revert-tx-${transaction.invoice_number}`}
                                                            className="text-gray-400 hover:text-[#8B5CF6]"
                                                            title="Revert to Draft (Admin)"
                                                        >
                                                            <Undo2 size={16} />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(transaction.id)}
                                                        data-testid={`delete-tx-${transaction.invoice_number}`}
                                                        className="text-gray-400 hover:text-red-500"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Admin-only Revert-to-Draft confirmation. Not rendered
                for non-admin roles; the trigger button is gated
                separately in the row-actions area. */}
            <AlertDialog
                open={!!revertTarget}
                onOpenChange={(open) => { if (!open) setRevertTarget(null); }}
            >
                <AlertDialogContent data-testid="revert-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-[#8B5CF6]">
                            <Undo2 size={20} /> Revert invoice to Draft?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm text-gray-600">
                                <p>
                                    This is an <strong>administrative recovery action</strong>.
                                    Invoice <span className="font-mono font-semibold text-[#1A1A1A]">
                                        {revertTarget?.invoice_number}
                                    </span> will move back to
                                    the Drafts section and become editable again.
                                </p>
                                <p>
                                    Stock quantities that were decremented at finalize
                                    will be automatically restored, and the round-trip
                                    is recorded in the audit ledger.
                                </p>
                                <p className="text-xs text-[#8B5CF6] bg-[#8B5CF6]/10 rounded px-3 py-2">
                                    Use this only to fix an accidental finalization —
                                    finalized invoices should normally stay locked.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="revert-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRevertToDraft}
                            disabled={reverting}
                            data-testid="revert-confirm-btn"
                            className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
                        >
                            {reverting && <Loader2 size={14} className="mr-2 animate-spin" />}
                            Revert to Draft
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Transaction Detail Dialog */}
            <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between pr-6">
                            <span className="flex items-center gap-3">
                                Transaction Details
                                {selectedTransaction && getTypeBadge(selectedTransaction.type || 'sale')}
                            </span>
                            <div className="flex items-center gap-2">
                                {selectedTransaction && (
                                    <SendEmailButton
                                        testId="email-from-modal"
                                        defaultTo={selectedTransaction.customer_email || ''}
                                        title={`Send ${selectedTransaction.invoice_number} by email`}
                                        description={selectedTransaction.customer_email
                                            ? `Sends to ${selectedTransaction.customer_email} by default.`
                                            : 'Enter a recipient email address.'}
                                        onSend={async (to) => {
                                            await salesAPI.sendEmail(selectedTransaction.id, to ? { to } : {});
                                        }}
                                    />
                                )}
                                <Button
                                    size="sm"
                                    onClick={() => selectedTransaction && printTransaction(selectedTransaction, { language: selectedTransaction.customer_document_language || 'fr' })}
                                    className="bg-[#1A1A1A] hover:bg-[#C08478] text-white"
                                    data-testid="print-from-modal"
                                >
                                    <Printer size={14} className="mr-2" />
                                    Print
                                </Button>
                                {selectedTransaction && (
                                    <PackingListMenu transactionId={selectedTransaction.id} testIdPrefix="pkg-modal" />
                                )}
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedTransaction && (
                        <div className="space-y-4 py-4">
                            {/* Invoice & Amount */}
                            <div className="bg-[#F5F5F5] rounded-xl p-4">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-500">Invoice Number</span>
                                    <span className="font-mono font-bold">{selectedTransaction.invoice_number}</span>
                                </div>
                                {selectedTransaction.subtotal != null && (
                                    <>
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-gray-500 text-sm">Subtotal</span>
                                            <span className="font-mono">{formatCurrency(selectedTransaction.subtotal)}</span>
                                        </div>
                                        {selectedTransaction.discount_amount > 0 && (
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-gray-500 text-sm">
                                                    Discount
                                                    {selectedTransaction.discount_type === 'percent' && selectedTransaction.discount_value
                                                        ? ` (${selectedTransaction.discount_value}%)`
                                                        : ''}
                                                </span>
                                                <span className="font-mono text-[#E76F51]">
                                                    -{formatCurrency(selectedTransaction.discount_amount)}
                                                </span>
                                            </div>
                                        )}
                                        {selectedTransaction.delivery_charge > 0 && (
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-gray-500 text-sm">Delivery</span>
                                                <span className="font-mono">{formatCurrency(selectedTransaction.delivery_charge)}</span>
                                            </div>
                                        )}
                                        {selectedTransaction.vat_breakdown && Object.keys(selectedTransaction.vat_breakdown).length > 0 ? (
                                            Object.entries(selectedTransaction.vat_breakdown).map(([rate, amount]) => (
                                                <div key={rate} className="flex justify-between items-center mb-1">
                                                    <span className="text-gray-500 text-sm">VAT ({rate}%)</span>
                                                    <span className="font-mono">{formatCurrency(amount)}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-gray-500 text-sm">VAT ({selectedTransaction.vat_rate || 0}%)</span>
                                                <span className="font-mono">{formatCurrency(selectedTransaction.vat_amount)}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="flex justify-between items-center mb-2 pt-2 border-t border-[#E5E5E5]">
                                    <span className="text-gray-500 font-medium">Total</span>
                                    <span className="text-2xl font-bold text-[#1A1A1A] euro-amount">
                                        {formatCurrency(selectedTransaction.amount)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-500">Payment</span>
                                    {getPaymentBadge(selectedTransaction.payment_method)}
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Date &amp; Time</span>
                                    <span className="font-medium">{formatDateTime(selectedTransaction.created_at)}</span>
                                </div>
                            </div>

                            {/* Line Items (Invoice) */}
                            {selectedTransaction.line_items && selectedTransaction.line_items.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
                                        <Package size={16} className="text-[#C08478]" />
                                        Line Items
                                    </h4>
                                    <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-[#F5F5F5]">
                                                    <TableHead className="text-xs">Item</TableHead>
                                                    <TableHead className="text-xs text-right">Qty</TableHead>
                                                    <TableHead className="text-xs text-right">Price</TableHead>
                                                    <TableHead className="text-xs text-right">Total</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {selectedTransaction.line_items.map((li, i) => (
                                                    <TableRow key={li.product_id || li.sku || `li-${i}`}>
                                                        <TableCell className="text-sm">
                                                            <div className="font-medium">{li.product_name}</div>
                                                            {li.sku && <div className="text-xs text-gray-400 font-mono">{li.sku}</div>}
                                                        </TableCell>
                                                        <TableCell className="text-right text-sm font-mono">{li.quantity}</TableCell>
                                                        <TableCell className="text-right text-sm font-mono">{formatCurrency(li.unit_price)}</TableCell>
                                                        <TableCell className="text-right text-sm font-mono font-semibold">{formatCurrency(li.line_total)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {/* Split Payments */}
                            {selectedTransaction.payments && selectedTransaction.payments.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-[#1A1A1A]">Payment Breakdown</h4>
                                    <div className="space-y-2">
                                        {selectedTransaction.payments.map((p, i) => (
                                            <div key={`pmt-${p.method}-${i}`} className="flex justify-between items-center p-3 bg-[#FAFAFA] rounded-lg">
                                                {getPaymentBadge(p.method)}
                                                <span className="font-mono font-semibold">{formatCurrency(p.amount)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Multiple Cheques (Split Payment) */}
                            {selectedTransaction.cheques && selectedTransaction.cheques.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-[#8B5CF6]">Cheque Details ({selectedTransaction.cheques.length})</h4>
                                    <div className="space-y-2">
                                        {selectedTransaction.cheques.map((c, i) => (
                                            <div key={c.id || c.cheque_number || `chq-${i}`} className="bg-[#8B5CF6]/5 rounded-lg p-3 space-y-1 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Bank</span>
                                                    <span className="font-medium">{c.bank_name}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Cheque #</span>
                                                    <span className="font-mono">{c.cheque_number}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Deposit Date</span>
                                                    <span>{formatDate(c.deposit_date)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Amount</span>
                                                    <span className="font-mono font-semibold">{formatCurrency(c.amount)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Status</span>
                                                    <Badge
                                                        className={
                                                            c.status === 'cleared' ? 'bg-[#2A9D8F] text-white' :
                                                            c.status === 'bounced' ? 'bg-[#E76F51] text-white' :
                                                            c.status === 'deposited' ? 'bg-[#264653] text-white' :
                                                            'bg-[#E9C46A] text-[#1A1A1A]'
                                                        }
                                                    >
                                                        {c.status || 'pending'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Single Cheque (Legacy) */}
                            {selectedTransaction.payment_method === 'cheque' && selectedTransaction.cheque_bank_name && (
                                <div className="space-y-3">
                                    <h4 className="font-semibold text-[#8B5CF6]">Cheque Details</h4>
                                    <div className="bg-[#8B5CF6]/5 rounded-lg p-4 space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Bank Name</span>
                                            <span className="font-medium">{selectedTransaction.cheque_bank_name || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Cheque Number</span>
                                            <span className="font-mono">{selectedTransaction.cheque_number || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Deposit Date</span>
                                            <span className="font-medium">
                                                {selectedTransaction.cheque_deposit_date ? formatDate(selectedTransaction.cheque_deposit_date) : '-'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Status</span>
                                            <Badge className={
                                                selectedTransaction.cheque_status === 'cleared' ? 'bg-[#2A9D8F] text-white' :
                                                selectedTransaction.cheque_status === 'bounced' ? 'bg-[#E76F51] text-white' :
                                                selectedTransaction.cheque_status === 'deposited' ? 'bg-[#264653] text-white' :
                                                'bg-[#E9C46A] text-[#1A1A1A]'
                                            }>
                                                {selectedTransaction.cheque_status || 'Pending'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Company Details */}
                            <div className="space-y-3">
                                <h4 className="font-semibold text-[#1A1A1A]">Customer Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="flex items-start gap-3 p-3 bg-[#FAFAFA] rounded-lg">
                                        <Building size={18} className="text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-500">Company</p>
                                            <p className="font-medium text-sm">{selectedTransaction.company_name || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 bg-[#FAFAFA] rounded-lg">
                                        <User size={18} className="text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-500">Contact</p>
                                            <p className="font-medium text-sm">{selectedTransaction.contact_person || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 bg-[#FAFAFA] rounded-lg">
                                        <Phone size={18} className="text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-500">Mobile</p>
                                            <p className="font-medium text-sm">{selectedTransaction.mobile_number || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 bg-[#FAFAFA] rounded-lg">
                                        <Mail size={18} className="text-gray-400 mt-0.5" />
                                        <div>
                                            <p className="text-xs text-gray-500">Email</p>
                                            <p className="font-medium text-sm">{selectedTransaction.email_address || '-'}</p>
                                        </div>
                                    </div>
                                    {selectedTransaction.company_address && (
                                        <div className="flex items-start gap-3 p-3 bg-[#FAFAFA] rounded-lg md:col-span-2">
                                            <MapPin size={18} className="text-gray-400 mt-0.5" />
                                            <div>
                                                <p className="text-xs text-gray-500">Address</p>
                                                <p className="font-medium text-sm">{selectedTransaction.company_address}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Notes */}
                            {selectedTransaction.notes && (
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-[#1A1A1A]">Notes</h4>
                                    <p className="text-gray-600 bg-[#FAFAFA] rounded-lg p-3 text-sm">
                                        {selectedTransaction.notes}
                                    </p>
                                </div>
                            )}

                            {/* Recorded By */}
                            <div className="text-sm text-gray-500 pt-2 border-t">
                                Recorded by: <span className="font-medium">{selectedTransaction.created_by_name}</span>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Transactions;
